import { motion } from 'framer-motion';
import { CircleCheck, OctagonAlert, PauseCircle, WifiOff, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { STATUS_LEGEND } from '@/lib/vehicle-status';
import { useUiStore } from '@/stores/ui-store';
import type { LiveStatus } from '@/types/api';

const CARDS: {
  status: LiveStatus;
  label: string;
  icon: LucideIcon;
  chip: string;
  value: string;
}[] = [
  { status: 'moving', label: 'Moving', icon: CircleCheck, chip: 'bg-moving-soft text-moving', value: 'text-foreground' },
  { status: 'idle', label: 'Idle', icon: PauseCircle, chip: 'bg-idle-soft text-idle', value: 'text-foreground' },
  { status: 'offline', label: 'Offline', icon: WifiOff, chip: 'bg-offline-soft text-offline', value: 'text-foreground' },
  { status: 'alert', label: 'Alerts', icon: OctagonAlert, chip: 'bg-alert-soft text-alert', value: 'text-alert' },
];

/**
 * The four fleet counts, floated over the top of the map. They double as the
 * status filter — clicking one narrows both the map and the panel, which is how
 * an operator actually uses a summary row.
 */
export function FleetStatCards({
  counts,
  className,
}: {
  counts: Record<LiveStatus, number>;
  className?: string;
}) {
  const statusFilter = useUiStore((s) => s.statusFilter);
  const toggle = useUiStore((s) => s.toggleStatusFilter);

  return (
    <div className={cn('pointer-events-auto flex flex-wrap gap-2.5', className)}>
      {CARDS.map((card, index) => {
        const active = statusFilter.includes(card.status);
        const dimmed = statusFilter.length > 0 && !active;

        return (
          <motion.button
            key={card.status}
            type="button"
            onClick={() => toggle(card.status)}
            aria-pressed={active}
            title={STATUS_LEGEND[card.status].full}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: dimmed ? 0.55 : 1, y: 0 }}
            transition={{ duration: 0.26, delay: index * 0.04, ease: [0.2, 0.8, 0.2, 1] }}
            className={cn(
              'flex min-w-[132px] flex-1 flex-col rounded-xl bg-card/95 px-3.5 py-3 text-left shadow-panel backdrop-blur transition-all',
              active ? 'ring-2 ring-primary/45' : 'hover:shadow-float',
            )}
          >
            <span className="flex w-full items-start justify-between gap-3">
              <span className="min-w-0">
                <span className="block truncate text-[12px] font-medium text-muted-foreground">
                  {card.label}
                </span>
                <span className={cn('tnum mt-0.5 block text-2xl leading-none font-bold', card.value)}>
                  {counts[card.status]}
                </span>
              </span>
              <span
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-lg',
                  card.chip,
                )}
              >
                <card.icon className="size-4.5" strokeWidth={2.1} />
              </span>
            </span>
            <span className="mt-1.5 block text-[10.5px] leading-tight text-muted-foreground">
              {STATUS_LEGEND[card.status].short}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
}
